/**
 * ResourceDashboard — main container for the Resources view.
 *
 * Layout:
 * - Resource Summary Cards (5 KPIs: FTE Demand, Supply, Gap, Top Consumer, Utilization)
 * - Accordion FTE Chart (initiative-grouped, expand/collapse)
 */

import { useMemo } from 'react';
import { useAdjacencyStore } from '../../store/adjacency-store';
import { computeResourceMetrics } from '../../types/resource-config';
import { nodePassesFilter } from '../../utils/filter-nodes';
import { ResourceSummaryCards } from './ResourceSummaryCards';
import { ResourceAccordionChart } from './ResourceAccordionChart';
import type { BudgetProject } from '../../utils/budget-import';
import budgetData from '../../data/budget-2026.json';

const budgetProjects = budgetData.projects as BudgetProject[];

export function ResourceDashboard() {
  const { nodes, filters, resourceConfig } = useAdjacencyStore();

  const allFiltered = useMemo(
    () => nodes.filter((n) => nodePassesFilter(n, filters)),
    [nodes, filters],
  );

  const financeNodes = useMemo(
    () => allFiltered.filter(
      (n) => n.budgetProjectName != null || n.iRnD != null || n.fRnD != null,
    ),
    [allFiltered],
  );

  const grandTotalInvestment = useMemo(
    () => financeNodes.reduce((sum, n) => sum + (n.iRnD ?? 0) + (n.fRnD ?? 0), 0),
    [financeNodes],
  );

  const metrics = useMemo(
    () => computeResourceMetrics(resourceConfig, grandTotalInvestment),
    [resourceConfig, grandTotalInvestment],
  );

  // Find top consumer for KPI tile
  const topConsumer = useMemo(() => {
    const bpMap = new Map<string, BudgetProject>();
    for (const bp of budgetProjects) bpMap.set(bp.project, bp);

    let top: { name: string; fte: number; pctOfDemand: number } | null = null;
    const effectiveCost = resourceConfig.avgFteCost * resourceConfig.fteEfficiency;
    if (effectiveCost === 0 || metrics.ftesNeeded === 0) return null;

    for (const n of financeNodes) {
      const totalInv = (n.iRnD ?? 0) + (n.fRnD ?? 0);
      if (totalInv === 0) continue;
      const fte = totalInv / effectiveCost;
      if (!top || fte > top.fte) {
        top = {
          name: n.name,
          fte: Math.round(fte * 100) / 100,
          pctOfDemand: (fte / metrics.ftesNeeded) * 100,
        };
      }
    }
    return top;
  }, [financeNodes, resourceConfig, metrics.ftesNeeded]);

  return (
    <div className="flex-1 min-h-0 overflow-auto">
      <div className="space-y-3" style={{ minWidth: '900px' }}>
        {/* KPI Summary Cards (includes utilization gauge tile) */}
        <ResourceSummaryCards
          resourceConfig={resourceConfig}
          metrics={metrics}
          topConsumer={topConsumer}
        />

        {/* Accordion FTE Chart */}
        <ResourceAccordionChart
          nodes={financeNodes}
          allNodes={nodes}
          metrics={metrics}
        />
      </div>
    </div>
  );
}
